import React, { FunctionComponent, useContext, useEffect, useMemo } from "react";
import { Grid, Stack } from "@mui/material";
import { Link, makeStyles, Theme, Typography } from "@material-ui/core";
import ArrowForwardIcon from "@material-ui/icons/ArrowForward";
import { FeedbackType, useStorydataForQuestionLazyQuery } from "../../../api/smartmelts-api/graphql/generated";
import { QuestionSectionLoadingIndicator } from "../question/question-section-loading-indicator";
import { Trans, useTranslation } from "react-i18next";
import { AnswerContext } from "./context/answer-context";
import { NutritionContext } from "../context/nutrition-context";
import { IdeaIcon } from "../../../assets/icons/idea-icon";
import { PositiveIcon } from "../../../assets/icons/positive-icon";
import { NegativeIcon } from "../../../assets/icons/negative-icon";
import { MenuItemCategory, MenuItemSubcategory } from "../../../models/types/category-types";
import { HeadingText } from "../../../components/text/heading-text";
import { ContentText } from "../../../components/text/content-text";
import { MenuSubcategoryDictionary } from "../../../models/categories";

const useStyles = makeStyles((theme: Theme) => ({
  container: {
    flex: 1,
    width: "100%",
  },
  explainHeader: {
    fontWeight: 700,
    fontSize: 21,
    marginBottom: theme.spacing(2),
  },
  padTop: {
    paddingTop: theme.spacing(2),
  },
  subheader: {
    fontWeight: 700,
    fontSize: 18,
  },
  emphasized: {
    color: theme.palette.primary.main,
    fontWeight: 700,
  },
}));

interface IStoryDataRecommendationProps {}

export const StoryDataRecommendation: FunctionComponent<IStoryDataRecommendationProps> = (props) => {
  const { t } = useTranslation();
  const { activeAnswer } = useContext(AnswerContext);
  const { isDemo } = useContext(NutritionContext);
  const classes = useStyles({ color: activeAnswer });

  const { changeActiveCategories, activeSubcategory, scrollTop } = useContext(NutritionContext);

  const [storydataQuery, { loading, data }] = useStorydataForQuestionLazyQuery({
    onCompleted: () => {
      scrollTop();
    },
  });

  useEffect(() => {
    if (activeAnswer) {
      storydataQuery({ variables: { questionId: activeAnswer.id, demo: isDemo } });
    }
  }, [storydataQuery, activeAnswer, isDemo]);

  const { description, isResultGood, resultBody, resultInfoBody } = useMemo(() => {
    if (data?.storydataForQuestion) {
      const isResultGood = data.storydataForQuestion.genestoryResult === "GOOD";
      return {
        isResultGood,
        description: (
          <Trans
            i18nKey={`twin_communication.${data.storydataForQuestion.description}.${FeedbackType.STORYDATA}.description`}
            components={[<span className={classes.emphasized} />]}
          />
        ),
        resultBody: isResultGood
          ? t(`twin_communication.${data.storydataForQuestion.description}.${FeedbackType.STORYDATA}.GOOD`)
          : t(`twin_communication.${data.storydataForQuestion.description}.${FeedbackType.STORYDATA}.BAD`),
        resultInfoBody: isResultGood
          ? t(`twin_communication.${data.storydataForQuestion.description}.${FeedbackType.STORYRECOMMENDATION}.GOOD`)
          : t(`twin_communication.${data.storydataForQuestion.description}.${FeedbackType.STORYRECOMMENDATION}.BAD`),
      };
    }
    return {
      description: undefined,
      isResultGood: undefined,
      resultBody: undefined,
      resultInfoBody: undefined,
    };
  }, [data, t, classes]);

  return (
    <Grid container className={classes.container}>
      <Grid item container direction="row">
        <HeadingText>{t(`twin_communication.${activeAnswer.question}.header`)}</HeadingText>
      </Grid>

      <Grid container direction="column" spacing={3} alignItems="center">
        {!loading && (
          <>
            <Grid item container justifyContent="flex-start">
              <ContentText light>{description}</ContentText>
            </Grid>

            <Grid item container justifyContent="flex-start">
              <Stack direction="row" spacing={2}>
                <ArrowForwardIcon color="primary" />
                <Typography color="primary">
                  <Link
                    variant="inherit"
                    href=""
                    onClick={(event: React.SyntheticEvent) => {
                      event.preventDefault();
                      changeActiveCategories(
                        MenuItemCategory.RECOMMENDATIONS,
                        MenuItemSubcategory.GENEVARIATIONS,
                        2,
                        MenuSubcategoryDictionary[activeSubcategory].geneDataGroupTypes[0],
                      );
                    }}
                  >
                    {t("twin_communication.link_to_genes")}
                  </Link>
                </Typography>
              </Stack>
            </Grid>

            <Grid item container direction="row" justifyContent="flex-start" spacing={2}>
              <Grid item>
                {isResultGood && <PositiveIcon width="40px" height="40px" />}
                {!isResultGood && <NegativeIcon width="40px" height="40px" />}
              </Grid>
              <Grid item container xs={12} sm>
                <Typography>{resultBody}</Typography>
              </Grid>
            </Grid>

            <Grid item container direction="row" justifyContent="flex-start" spacing={2}>
              <Grid item>
                <IdeaIcon width="40px" height="40px" />
              </Grid>
              <Grid item container xs={12} sm>
                <Typography>{resultInfoBody}</Typography>
              </Grid>
            </Grid>
          </>
        )}
        {loading && <QuestionSectionLoadingIndicator />}
      </Grid>
    </Grid>
  );
};
